import React, { useEffect, useMemo, useRef, useState } from 'react'
import T from 'prop-types'
import {
  TextField,
  Popover,
  Paper,
  Stack,
  styled,
  IconButton,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DateCalendar, PickersDay } from '@mui/x-date-pickers'
import getYearMonthDayDate from 'lib/getYearMonthDayDate'
import { formatDate } from 'lib/dateLib'
import { isSameDay, isWithinInterval } from 'date-fns'
import { Clear } from '@mui/icons-material'

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== 'inRange' &&
    prop !== 'isStart' &&
    prop !== 'isEnd' &&
    prop !== 'isTemporary',
})(({ theme, inRange, isStart, isEnd, day }) => ({
  ...((inRange || isStart || isEnd) && {
    backgroundColor:
      isStart || isEnd
        ? theme.palette.primary.light
        : theme.palette.action.hover,

    '&:hover, &:focus': {
      backgroundColor:
        isStart || isEnd
          ? theme.palette.primary.light
          : theme.palette.action.hover,
    },
    borderRadius: 0,
  }),
  ...(isStart && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(isEnd && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
}))

function Day(props) {
  const { day, inRange, isStart, isEnd, ...other } = props

  return (
    <CustomPickersDay
      {...other}
      day={day}
      selected={false}
      disableMargin
      inRange={inRange}
      isStart={isStart}
      isEnd={isEnd}
    />
  )
}

export default function FilterDateRange({
  value,
  handleChange,
  readOnly,
  ...others
}) {
  const { t } = useTranslation()

  const [localValue, setLocalValue] = useState([null, null])
  const [index, setIndex] = useState(0)
  const [open, setOpen] = useState(false)

  const ref = useRef(null)

  const label = useMemo(() => {
    return (
      (localValue[0] ? `Da ${formatDate(t, localValue[0])}` : '') +
      (localValue[1] ? ` a ${formatDate(t, localValue[1])}` : '')
    )
  }, [localValue, t])

  const rangeIsSet = useMemo(() => localValue[0] && localValue[1], [localValue])

  useEffect(() => {
    setLocalValue((value || [null, null]).map((date) => date && new Date(date)))
  }, [value])

  const handleChangeLocal = (newValue) => {
    const rangeIsSet = newValue.every((value) => value)
    const rangeIsEmpty = newValue.every((value) => !value)
    if (rangeIsSet) {
      newValue.sort((a, b) => a - b)
    }
    setLocalValue(newValue)
    if (rangeIsSet || rangeIsEmpty) {
      handleChange(
        ...newValue.map((value) => (value ? getYearMonthDayDate(value) : null))
      )
    }
  }

  return (
    <>
      <TextField
        ref={ref}
        size="small"
        inputProps={{
          value: label,
          readOnly: true,
          placeholder: t('common.date.placeholderDMY'),
        }}
        label={undefined}
        onClick={(e) => setOpen(true)}
      />
      <Popover
        open={open}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <DateCalendar
          value={localValue[index]}
          onChange={(newValue) => {
            const newLocalValue = [...localValue]
            newLocalValue[index] = newValue
            handleChangeLocal(newLocalValue)
            if (index === 0) {
              setIndex(1)
            }
          }}
          disableHighlightToday
          slots={{ day: Day }}
          slotProps={{
            day: (ownerState) => ({
              inRange:
                rangeIsSet &&
                isWithinInterval(ownerState.day, {
                  start: localValue[0],
                  end: localValue[1],
                }),
              isStart: isSameDay(ownerState.day, localValue[0]),
              isEnd: isSameDay(ownerState.day, localValue[1]),
            }),
          }}
        />
        <Paper
          variant="outlined"
          tabIndex={-1}
          sx={{
            borderTopWidth: 1,
            p: 1,
          }}
        >
          <Stack
            gap={1}
            direction="row"
            justifyContent="space-between"
            sx={{ width: 300 }}
          >
            <TextField
              value={formatDate(t, localValue[0])}
              label={others?.startText || t('common.form.from')}
              inputProps={{ readOnly: true }}
              size="small"
              focused={index === 0}
              onClick={() => setIndex(0)}
            />
            <TextField
              value={formatDate(t, localValue[1])}
              label={others.endText || t('common.form.to')}
              inputProps={{ readOnly: true }}
              size="small"
              focused={index === 1}
              onClick={() => setIndex(1)}
            />
            <IconButton
              onClick={() => {
                handleChangeLocal([null, null])
                setIndex(0)
              }}
            >
              <Clear />
            </IconButton>
          </Stack>
        </Paper>
      </Popover>
    </>
  )
}

FilterDateRange.propTypes = {
  value: T.array.isRequired,
  handleChange: T.func.isRequired,
  readOnly: T.bool,
  others: T.object,
}
